import type { UtilityContext } from '@change-corgi/core/react/utilityContext';

import { NotFoundError } from 'src/shared/error';

import {
	FCM_COMBO_PROMOTER_CONTRIBUTION_IMPACT_VALUES,
	FCM_CREAM_CHIP_IN_VALUES,
	FCM_PROMOTION_COUNTRY_GRADUAL_OPENING,
} from 'src/app/shared/fcm/configs';

import type { PsfPromoteOrShareDataQuery, PsfPromoteOrShareDataQueryVariables } from './api.graphql';
import { PsfPromoteOrShareData } from './api.graphql';
import { handleRedirect } from './handleRedirect';
import { normalizeData } from './normalizeData';
import { getRecruiter } from './recruiter';
import type { PsfPromoteOrSharePageData } from './types';
import { PsfPromoteOrSharePageDataError } from './types';

export type { PsfPromoteOrSharePageData };

export async function getPsfPromoteOrSharePageData(
	utilityContext: UtilityContext,
	slug: string,
	{
		recruiterId,
		banditOverrides,
	}: {
		recruiterId?: string;
		banditOverrides: { combo?: string };
	},
): Promise<PsfPromoteOrSharePageData> {
	const { gql, fcm } = utilityContext;

	const [{ data }, { data: recruiterData }, fcmValues] = await Promise.all([
		gql.fetch<PsfPromoteOrShareDataQuery, PsfPromoteOrShareDataQueryVariables>({
			query: PsfPromoteOrShareData,
			variables: {
				slugOrId: slug,
				includeBanditComboOverride: !!banditOverrides.combo,
				banditComboOverrideVariantName: banditOverrides.combo || '',
			},
			rejectOnError: true,
		}),
		recruiterId ? getRecruiter(utilityContext, recruiterId) : { data: undefined },
		fcm.get({
			chipInAmount: FCM_CREAM_CHIP_IN_VALUES,
			joinCommunityFooterConfig: FCM_COMBO_PROMOTER_CONTRIBUTION_IMPACT_VALUES,
			gradualOpening: FCM_PROMOTION_COUNTRY_GRADUAL_OPENING,
		}),
	]);

	if (!data?.petition) {
		throw new NotFoundError();
	}

	if (!data?.petition.promotionStats) {
		throw new PsfPromoteOrSharePageDataError('psf_promote_or_share_page_missing_promotion_stats');
	}

	handleRedirect(utilityContext, { petition: data.petition, viewer: data.viewer, fcmValues });

	return normalizeData(utilityContext, {
		petition: data.petition,
		promotionStats: data.petition.promotionStats,
		viewer: data.viewer,
		recruiter: recruiterData?.recruiter || undefined,
		fcmValues,
	});
}
